import {
  formatCourierPackageTag,
  getOrderAmountDisplay,
  getOrderFulfillmentMethod,
  useTranslation,
  formatPrice,
  OrderStatusBadge,
  isCourierAssignableOrder,
  isManualApprovalPending,
} from '@dukanti/shared';
import type { CourierOrderAssignmentSummary, StoreOrderRow, TranslationKey } from '@dukanti/shared';
import { ChevronRight } from 'lucide-react';
import { isUrgentOrder } from '../lib/is-urgent-order';

export interface OrdersMobileCardsProps {
  readonly rows: readonly StoreOrderRow[];
  readonly onCardClick: (orderId: string) => void;
  readonly selectedOrderIds?: ReadonlySet<string>;
  readonly canSelectOrders?: boolean;
  readonly onSelectionChange?: (orderId: string, selected: boolean) => void;
}

type OrderRowWithAssignment = StoreOrderRow & {
  readonly courier_assignment?: CourierOrderAssignmentSummary | null;
};

function courierProofStatusKey(assignment: CourierOrderAssignmentSummary): TranslationKey {
  if (assignment.proof_status === 'internally_confirmed') {
    return 'admin.orders.courierProof.confirmed' as TranslationKey;
  }
  if (
    assignment.proof_status === 'submitted' ||
    assignment.proof_status === 'awaiting_internal_confirmation'
  ) {
    return 'admin.orders.courierProof.awaitingConfirmation' as TranslationKey;
  }
  return 'admin.orders.courierProof.awaitingProof' as TranslationKey;
}

export function OrdersMobileCards({
  rows,
  onCardClick,
  selectedOrderIds,
  canSelectOrders = false,
  onSelectionChange,
}: OrdersMobileCardsProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(d);
  };

  return (
    <div dir={dir} data-testid="orders-mobile-cards" className="flex flex-col gap-3 md:hidden">
      {rows.map((row) => {
        const urgent = isUrgentOrder(row);
        const isAssignable = isCourierAssignableOrder(row);
        const isApprovalPending = isManualApprovalPending(row);
        const assignment =
          row.delivery_method === 'pickup'
            ? null
            : ((row as OrderRowWithAssignment).courier_assignment ?? null);
        const amount = getOrderAmountDisplay(row);
        return (
          <article
            key={row.id}
            data-testid="orders-mobile-card"
            className="border-border bg-card shadow-admin-card rounded-lg border p-4 text-start"
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {canSelectOrders ? (
                  <input
                    type="checkbox"
                    className="border-input text-admin-lime focus:ring-admin-lime size-4 rounded"
                    checked={selectedOrderIds?.has(row.id) ?? false}
                    disabled={!isAssignable}
                    aria-label={
                      isAssignable
                        ? t('admin.orders.courierAssignment.assign' as never)
                        : t('admin.orders.courierAssignment.notEligible' as never)
                    }
                    onChange={(event) => onSelectionChange?.(row.id, event.target.checked)}
                  />
                ) : null}
                <OrderStatusBadge status={row.status} method={getOrderFulfillmentMethod(row)} />
                <span className="text-muted-foreground text-xs">
                  {t(
                    getOrderFulfillmentMethod(row) === 'pickup'
                      ? 'fulfillment.pickup'
                      : 'fulfillment.delivery',
                  )}
                </span>
                {isApprovalPending ? (
                  <span
                    className="border-honey/40 bg-honey/10 text-foreground rounded-full border px-2 py-0.5 text-xs"
                    data-testid="orders-mobile-card-approval-pending"
                  >
                    {t('admin.orders.manualApproval.pendingBadge' as never)}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => onCardClick(row.id)}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-admin-lime rounded-md p-1 focus-visible:outline-none focus-visible:ring-2"
                aria-label={t('admin.orders.viewOrderAria', {
                  id: row.id.slice(0, 8),
                  customer: row.customer_name_snapshot,
                })}
              >
                <ChevronRight
                  className="rtl:rotate-180"
                  size={16}
                  strokeWidth={2}
                  aria-hidden="true"
                />
              </button>
            </div>
            <div className="mb-1 flex items-center gap-2">
              {urgent && (
                <span
                  data-testid="orders-mobile-card-urgent"
                  className="bg-honey inline-block h-2 w-2 rounded-full"
                  aria-label={t('admin.orders.urgentLabel')}
                  title={t('admin.orders.urgentLabel')}
                />
              )}
              <div className="text-foreground text-base font-medium">
                {row.customer_name_snapshot}
              </div>
            </div>
            {row.customer_phone_snapshot && (
              <div className="text-muted-foreground mb-2 text-xs">
                {row.customer_phone_snapshot}
              </div>
            )}
            {assignment ? (
              <div className="text-muted-foreground mb-2 space-y-1 text-xs">
                <div>
                  {t('admin.orders.courierAssignment.assignedTo' as never, {
                    name: assignment.courier_name,
                  })}
                </div>
                <div className="flex flex-wrap gap-1">
                  {assignment.package_tag ? (
                    <span>
                      {t('admin.orders.courierAssignment.packageTag' as never, {
                        tag: formatCourierPackageTag(assignment.package_tag),
                      })}
                    </span>
                  ) : null}
                  <span>{t(courierProofStatusKey(assignment))}</span>
                </div>
              </div>
            ) : null}
            <div className="flex items-center justify-between">
              <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-3 text-xs">
                <span>{formatDate(row.created_at)}</span>
                <span>{t('admin.orders.itemCount', { count: row.item_count })}</span>
              </div>
              <div className="font-display text-saffron ms-3 shrink-0 text-base font-semibold">
                {formatPrice(amount.amountCents, lang)}
                {!amount.confirmed ? (
                  <span className="text-muted-foreground block whitespace-normal text-xs font-normal">
                    {t(amount.confirmed ? 'checkout.total' : 'fulfillment.legacySubtotal')}
                  </span>
                ) : null}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
